import React, { useState, useEffect } from 'react';
import { Container, Form, InputGroup, Alert, Button } from 'react-bootstrap';
import LoaderButton from '../components/LoaderButton';
import LocalHelp from '../components/LocalHelp';
import AddressPicker from '../components/AddressPicker';
import { useAppContext } from '../libs/contextLib';
import { useFormFields } from '../libs/hooksLib';
import { UiError } from '../libs/errorLib';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";
import config from '../config';
import {
  getPromiseFromApi,
} from '../libs/integralLib';

export default function RequestAccount() {
  // Feature flag.
  const showDropdownServiceProvider = true;

  const { setError }                          = useAppContext();
  // We initialise to true so we don't start validatring the form until the user
  // has clicked submit the first time. This prevent sannoying messages form
  // showing until the user is 'done'.
  const [isValid, setIsValid]                               = useState(true);
  const [isLoading, setIsLoading]                           = useState(false);
  const [isSubmitted, setIsSubmitted]                       = useState(false);
  const { trackEvent }                                      = useMatomo();
  const [ captchaValue, setCaptchaValue ]                   = useState(null);
  const navigate                                            = useNavigate();
  const [fields, handleFieldChange]                         = useFormFields({
    title: '',
    firstName: '',
    lastName: '',
    type: '',
    irn: '',
    postcode: '',
    addressLine1: '',
    addressLine2: '',
    addressTown: '',
    addressCounty: '',
    addressCountry: '',
    email: '',
    mobile: '',
    landline: '',
    farFrn: '',
    fdaFrn: '',
    serviceProvider: '',
    serviceProviderOther: '',
    internalAgencyCode: '',
    otherFirms: false,
    submittedPast: false,
    notes: ''
  });

  /**
   * Called after fields is updated in handleFieldChange.
   * Will validate form if state currently isn't valid.
   */
  useEffect(() => {
      if (!isValid) {
        validateForm();
      }
    },
    // eslint-disable-next-line
    [isValid, fields, captchaValue]
  );

  // Determines whether one of the entered firms references is for SJP.
  function isSjp(fields) {
    return(
      fields.farFrn === '195351' ||
      fields.fdaFrn === '195351' ||
      fields.serviceProvider === '195351'
    );
  }

  // gets the Service provider from either the droipdown or free text field.
  function getServiceProvider() {
    if(fields.serviceProvider === 'Other') {
      return(`Other: ${fields.serviceProviderOther}`);
    }
    else {
      return(fields.serviceProvider);
    }
  }

  /**
   * Validates the form, sets error messages and isValid accordingly.
   */
  function validateForm() {
    let valid = true;
    let messages = [];
    // Build a message about unfilled fields.
    let unfilledFields = [];
    if (fields.title.length === 0) {
      unfilledFields.push("Title");
    }
    if (fields.firstName.length === 0) {
      unfilledFields.push("First name");
    }
    if (fields.lastName.length === 0) {
      unfilledFields.push("Last name");
    }
    if (fields.type.length === 0) {
      unfilledFields.push("Role type");
    }
    if (fields.postcode.length === 0) {
      unfilledFields.push("Postcode");
    }
    if (fields.addressLine1.length === 0) {
      unfilledFields.push("Address Line 1");
    }
    if (fields.addressTown.length === 0) {
      unfilledFields.push("Town");
    }
    if (fields.addressCounty.length === 0) {
      unfilledFields.push("County");
    }
    if (fields.email.length === 0) {
      unfilledFields.push("Business email address");
    }
    if (fields.mobile.length === 0) {
      unfilledFields.push("Business mobile phone");
    }
    if (fields.fdaFrn.length === 0) {
      unfilledFields.push("Directly Authorised FRN");
    }
    if (fields.serviceProvider.length === 0) {
      unfilledFields.push("Service Provider");
    }
    if (
      fields.serviceProvider === 'Other' &&
      fields.serviceProviderOther.length === 0
    ) {
      unfilledFields.push("Service Provider");
    }
    if (isSjp(fields) && fields.internalAgencyCode.length === 0) {
      unfilledFields.push("SJP Partner Code");
    }
    
    if (unfilledFields.length > 0) {
      messages.push(`Please ensure the ${unfilledFields.join(', ')} ${unfilledFields.length > 1 ? 'fields are' : 'field is'} filled.`);
      valid = false;
    }

    const irnRegex = /^[a-zA-Z]{3}[0-9]{5}$/;
    if (
      fields.irn.length !== 0 && !irnRegex.test(fields.irn)
    ) {
      valid = false;
      messages.push('Please ensure the entered individual reference numbers is 3 letters followed by 5 numbers.');
    }

    const numOnlyRegex = /^[0-9]+$/;

    if (
      !numOnlyRegex.test(fields.mobile)
    ) {
      valid = false;
      messages.push('Please ensure the mobile phone number contains only numbers.');
    }

    if (
      fields.landline.length !== 0 && !numOnlyRegex.test(fields.landline)
    ) {
      valid = false;
      messages.push('Please ensure the landline number contains only numbers.');
    }

    if (
      (
        fields.fdaFrn.length !== 6 &&
        fields.fdaFrn.length !== 7
      ) || !numOnlyRegex.test(fields.fdaFrn)
    ) {
      valid = false;
      messages.push('Please ensure the Directly Authorised FRN is 6 or 7 digits long.');
    }

    if (
      fields.farFrn.length !== 0 && (
        (
          fields.farFrn.length !== 6 &&
          fields.farFrn.length !== 7
        ) ||
        !numOnlyRegex.test(fields.farFrn)
      )
    ) {
      valid = false;
      messages.push('Please ensure the Authorised Representative FRN is either blank, or 6 or 7 digits long.');
    }

    // SJP Partner codes can be prepended with a P or J for paraplanners or
    // admin users. See: https://aurora-cmas.atlassian.net/browse/GW-4
    const sjpPartnerCodeRegex = fields.type === "adviser" ?
      /^(09|99|91|92|93|94|95|96|97|98)[0-9]{4}[P-Zp-z]$/ :
      /^[PJpj]{0,1}(09|99|91|92|93|94|95|96|97|98)[0-9]{4}[P-Zp-z]$/;
    
    if (
      isSjp(fields) &&
      fields.internalAgencyCode.length > 0 &&
      !sjpPartnerCodeRegex.test(fields.internalAgencyCode)
    ) {
      valid = false;
      messages.push('Please ensure the SJP Partner Code conforms to the specified format.');
    }

    if(captchaValue === null) {
      valid = false;
      messages.push('Please solve the captcha.');
    }

    // Potentially clear errors.
    if (valid) {
      setError(
        new UiError('requestAccount',  '', 'resolved')
      );
    }
    else {
      // Build an error message.
      const errorElement = <div>
        {
          messages.map((message) => {
            return (<><span>{ message }</span><br/></>);
          })
        }
      </div>
      setError(
        new UiError(
          'requestAccount', 
          errorElement
        )
      );
      // And scroll to the top.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsValid(valid);
    return valid;
  }

  /**
   * Function to handle the user hitting submit.
   * Registers the user and sets the user in the context.
   * @param {event} event 
   */
  async function handleSubmit(event) {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    // Track the event.
    trackEvent({
      category: 'identity',
      action: 'requestAccount',
      name: 'set',
    });
    // Post the data.
    getPromiseFromApi(
      `${config.api.address}/formdata`,
      'PUT',
      {
        type: 1,
        data: {
          title: fields.title,
          firstName: fields.firstName,
          lastName: fields.lastName,
          type: fields.type,
          irn: fields.irn,
          postcode: fields.postcode,
          addressLine1: fields.addressLine1,
          addressLine2: fields.addressLine2,
          addressTown: fields.addressTown,
          addressCounty: fields.addressCounty,
          addressCountry: fields.addressCountry,
          email: fields.email,
          mobile: fields.mobile,
          landline: fields.landline,
          farFrn: fields.farFrn,
          fdaFrn: fields.fdaFrn,
          serviceProvider: getServiceProvider(),
          internalAgencyCode: fields.internalAgencyCode,
          otherFirmsNew: fields.otherFirms,
          submittedPast: fields.submittedPast,
          notes: fields.notes
        }
      }
    )
    .then(
      ( { code, data } ) => {
        if(code === 200) {
          setError(
            new UiError('requestAccount', '', 'resolved')
          );
          setIsSubmitted(true);
        }
        else {
          setError(
            new UiError('requestAccount', data.error.message, "danger")
          );
        }
      }
    )
    .catch(
      message => {
        setError(
          new UiError('requestAccount', message)
        );
      }
    )
    .finally(
      () => {
        setIsLoading(false);
      }
    )
  }

  function getRegisterCard() {
    if (config.featureFlags.showFirmRegistration) {
      return (
        <>
          <h1 className="h3 mb-3 font-weight-normal text-center">Firm Registration</h1>
          <Alert variant="success">
            If your firm does not have terms of business with Cirencester
            Friendly, please register your firm&nbsp;
            <a
              href={config.links.firmRegistration}
            >
              here
            </a>
            &nbsp;before continuing.
          </Alert>
        </>
      )
    }
  }

  /**
   * Returns captcha if it's enabled.
   */
  function getCaptcha() {
    if (!config.captcha.enabled) {
      return(null);
    }
    return (
      <div className="recaptchaContainer">
        <ReCAPTCHA
          sitekey = { config.captcha.siteKey }
          onChange = { 
            (value) => {
              setCaptchaValue(value)
            }
          }
          onExpired = { () => setCaptchaValue(null) }
        />
      </div>
    )
  }

  /**
   * Returns the request form.
   */
  function getForm() {
    return (
      <div>
        <Form onSubmit={handleSubmit}>
          <div className="d-grid gap-2">
            <h5 className="mb-2 font-weight-normal text-center">Personal Information</h5>
            <div>
              <Form.Select
                autoFocus
                className="topinput"
                id="title"
                value={fields.title}
                onChange={handleFieldChange}
              >
                <option value="">&lt;Title*&gt;</option>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Dr">Dr</option>
                <option value="Miss">Miss</option>
                <option value="Ms">Ms</option>
                <option value="Admiral">Admiral</option>
                <option value="Brigadier">Brigadier</option>
                <option value="Captain">Captain</option>
                <option value="Colonel">Colonel</option>
                <option value="General">General</option>
                <option value="Group Captain">Group Captain</option>
                <option value="Lance Corporal">Lance Corporal</option>
                <option value="Lt. Colonel">Lt. Colonel</option>
                <option value="Major">Major</option>
                <option value="Master">Master</option>
                <option value="Professor">Professor</option>
                <option value="Mx">Mx</option>
                <option value="Reverend">Reverend</option>
                <option value="Wing Commander">Wing Commander</option>
                <option value="Canon">Canon</option>
                <option value="The Secretary">The Secretary</option>
                <option value="The Executor">The Executor</option>
              </Form.Select>
              <Form.Label htmlFor="title" className="form-label visually-hidden">Title</Form.Label>
              <Form.Control 
                className="middleinput"
                id="firstName"
                placeholder="First name*"
                type="text"
                autoComplete="fname"
                value={fields.firstName}
                onChange={handleFieldChange}
                required
              />
              <Form.Label htmlFor="firstName" className="form-label visually-hidden">First name</Form.Label>
              <Form.Control 
                id="lastName"
                className="middleinput"
                placeholder="Last name*"
                type="text"
                autoComplete="lname"
                value={fields.lastName}
                onChange={handleFieldChange}
                required
              />
              <Form.Label htmlFor="lastName" className="form-label visually-hidden">Last name</Form.Label>
              <InputGroup>
                <Form.Select
                  className="middleinput"
                  id="type"
                  value={fields.type}
                  onChange={handleFieldChange}
                >
                  <option value="">&lt;I am a...*&gt;</option>
                  <option value="adviser">Financial Adviser</option>
                  <option value="administrator">Firm Administrator</option>
                  <option value="paraplanner">Paraplanner</option>
                </Form.Select>
                <Form.Label htmlFor="type" className="form-label visually-hidden">Role type</Form.Label>
                <InputGroup.Text className="middleinput">
                  <LocalHelp
                    markdown={`## What are the different role types?
### Financial Adviser
A Financial Adviser can quote and apply for our products under their own name.
They are not able to do this on behalf of other Financial Advisers.

### Paraplanner
A Paraplanner can quote and apply for our products on behalf of Financial
Advisers within their Firm, as well as viewing applications and documents
completed by the Financial Advisers themselves.  They can also  amend and
update the Financial Advisers applications and documents.

### Firm Administrator
A Firm Administrator can create new Paraplanner roles within their Firm and
determine which Financial Advisers a Paraplanner can act on behalf of.
The Firm Administrator can also view their firms Financial Advisers and
Paraplanners Applications and commission statements, but does not have the
permission to quote, apply or amend anything.
`}
                  />
                </InputGroup.Text>
              </InputGroup>  
              <InputGroup>
                <Form.Control 
                  id="irn"
                  className="bottominput"
                  placeholder="Individual Reference Number (if known)"
                  type="text"
                  value={fields.irn}
                  onChange={handleFieldChange}
                />
                <Form.Label htmlFor="lastName" className="form-label visually-hidden">Individual Reference Number (if known)</Form.Label>
                <InputGroup.Text className="bottominput">
                  <LocalHelp
                    markdown={`## What is an Individual Reference Number (IRN)?
When you register with the FCA you are given a unique identifier known as an 'Individual Reference Number' or 'IRN'. This number usually consists of three letters and five numbers, example AAA12345.

If you do not currently have an IRN, the field can be left blank and advised in the future should you receive one.

If you are unsure of your firm's or your details, please visit https://register.fca.org.uk/s/ which will assist in a search for further details.

Please contact our Agency Team on [01285 652 492](tel:01285652492) if you require any further assistance.`}
                  />
                </InputGroup.Text>
              </InputGroup>
            </div>
          </div>
        </Form>
        <AddressPicker
          formTitle             = "Address"
          id                    = "CorrespondenceAddress"
          className             = "mb-2"
          handleFieldChange     = {handleFieldChange}
          postcode              = {fields.postcode}
          addressLine1          = {fields.addressLine1}
          addressLine2          = {fields.addressLine2}
          addressTown           = {fields.addressTown}
          addressCounty         = {fields.addressCounty}
          addressCountry        = {fields.addressCountry}
          postcodeFieldId       = 'postcode'
          addressLine1FieldId   = 'addressLine1'
          addressLine2FieldId   = 'addressLine2'
          addressTownFieldId    = 'addressTown'
          addressCountyFieldId  = 'addressCounty'
          addressCountryFieldId = 'addressCountry'
        />
        <Form onSubmit={handleSubmit}>
          <div className="d-grid gap-2">
            <div>
              <InputGroup>
                <Form.Control 
                  id="email"
                  className="topinput"
                  placeholder="Business Email Address*"
                  type="email"
                  autoComplete="username"
                  value={fields.email}
                  onChange={handleFieldChange}
                  required
                />
                <Form.Label htmlFor="email" className="form-label visually-hidden">Business Email Address</Form.Label>
                <InputGroup.Text className="topinput">
                  <LocalHelp
                    markdown={`## Email Address
The email address must:
* be unique to you (not from a shared mailbox or belonging to another person)
* be tied to the firm through which the Adviser is conducting business e.g. name.surname@firmname.co.uk

Please contact our Agency Team on [01285 652 492](tel:01285652492) if you require any further assistance.`}
                  />
                </InputGroup.Text>
              </InputGroup>
              <Form.Control 
                id="mobile"
                className="middleinput"
                placeholder="Business Mobile Phone*"
                type="tel"
                autoComplete="tel"
                value={fields.mobile}
                onChange={handleFieldChange}
                required
              />
              <Form.Label htmlFor="mobile" className="form-label visually-hidden">Business Mobile phone</Form.Label>
              <Form.Control 
                id="landline"
                className="bottominput"
                placeholder="Business Landline Number"
                type="tel"
                value={fields.landline}
                onChange={handleFieldChange}
              />
              <Form.Label htmlFor="landline" className="form-label visually-hidden">Business Landline Number</Form.Label>
            </div>
            <h5 className="my-2 font-weight-normal text-center">Firm Information</h5>
            <div>
              <InputGroup>
                <Form.Control 
                  className="topinput"
                  id="farFrn"
                  placeholder="Appointed Representative FRN"
                  type="number"
                  value={fields.farFrn}
                  onChange={handleFieldChange}
                />
                <InputGroup.Text className="topinput">
                  <LocalHelp
                    markdown={`## What is a Firm Reference Number (FRN)?
When a firm registers with the FCA, they are given a unique identifier known as a 'Firm Reference Number' or 'FRN'. This currently consists of six numbers, example 123456.

As an Appointed Representative (AR) of a Directly Authorised (DA) firm or network you will be required to enter the FRN for both firms in the fields provided.

If working for a Directly Authorised (DA) firm only, you will be required to enter the FRN for the firm you immediately work for to complete the registration.

This will enable us to ensure new registrations are placed against the right firm and commission payments are processed correctly. 

If you are unsure of the firm's FRN, please visit https://register.fca.org.uk/s/ which will assist in a search for further details.
Please contact our Agency Team on [01285 652 492](tel:01285652492) if you require any further assistance.
                    `}
                />
                </InputGroup.Text>
              </InputGroup>
              <Form.Label htmlFor="farFrn" className="form-label visually-hidden">Appointed Representative FRN</Form.Label>
              <InputGroup>
                <Form.Control 
                  className="middleinput"
                  id="fdaFrn"
                  placeholder="Directly Authorised FRN*"
                  type="number"
                  value={fields.fdaFrn}
                  onChange={handleFieldChange}
                  required
                />
                <InputGroup.Text className="middleinput">
                  <LocalHelp
                    markdown={`## What is a Firm Reference Number (FRN)?
When a firm registers with the FCA, they are given a unique identifier known as a 'Firm Reference Number' or 'FRN'. This currently consists of six numbers, example 123456.

As an Appointed Representative (AR) of a Directly Authorised (DA) firm or network you will be required to enter the FRN for both firms in the fields provided.

If working for a Directly Authorised (DA) firm only, you will be required to enter the FRN for the firm you immediately work for to complete the registration.

This will enable us to ensure new registrations are placed against the right firm and commission payments are processed correctly. 

If you are unsure of the firm's FRN, please visit https://register.fca.org.uk/s/ which will assist in a search for further details.
Please contact our Agency Team on [01285 652 492](tel:01285652492) if you require any further assistance.
                    `}
                  />
                </InputGroup.Text>
              </InputGroup>
              <Form.Label htmlFor="fdaFrn" className="form-label visually-hidden">Directly Authorised FRN</Form.Label>
              {
                showDropdownServiceProvider ? (
                  <>
                    <InputGroup>
                      <Form.Select
                        className={isSjp(fields) || fields.serviceProvider === 'Other' ? "middleinput" : "bottominput"}
                        id="serviceProvider"
                        value={fields.serviceProvider}
                        onChange={handleFieldChange}
                      >
                        <option value="">&lt;Service provider*&gt;</option>
                        <option value="None">None</option>
                        <option value="Auxilium Partnership Limited">Auxilium Partnership Limited</option>
                        <option value="Next Intelligence">Next Intelligence</option>
                        <option value="Paradigm Mortgage Services LLP">Paradigm Mortgage Services LLP</option>
                        <option value="Premier Mortgage Service">Premier Mortgage Service</option>
                        <option value="SimplyBiz Services Limited">SimplyBiz Services Limited</option>
                        <option value="TMA Club">TMA Club</option>
                        <option value="The Right DA Club">The Right DA Club</option>
                        <option value="Omni Protect">Omni Protect</option>
                        <option value="Brilliant Group">Brilliant Group</option>
                        <option value="Other">Other...</option>
                      </Form.Select>
                      <Form.Label htmlFor="serviceProvider" className="form-label visually-hidden">Service provider</Form.Label>
                      <InputGroup.Text
                        className={isSjp(fields) || fields.serviceProvider === 'Other' ? "middleinput" : "bottominput"
                      }>
                        <LocalHelp
                          markdown={`Please select the name of the Service Provider
      you work with. If you do not work with a Service Provider, please select 'None'.
                          `}
                        />
                      </InputGroup.Text>
                    </InputGroup>
                    <InputGroup>
                      {
                        fields.serviceProvider === 'Other' ? (
                          <Form.Control
                            className={isSjp(fields) ? "middleinput" : "bottominput"}
                            id="serviceProviderOther"
                            placeholder="Service Provider*"
                            type="text"
                            value={fields.serviceProviderOther}
                            onChange={handleFieldChange}
                            required = {fields.serviceProvider === 'Other'}
                          />
                        ) : null
                      }
                    </InputGroup>
                  </>
                ) : (
                <InputGroup>
                  <Form.Control 
                    className={isSjp(fields) ? "middleinput" : "bottominput"}
                    id="serviceProvider"
                    placeholder="Service Provider*"
                    type="text"
                    value={fields.serviceProvider}
                    onChange={handleFieldChange}
                    required
                  />
                  <InputGroup.Text className={isSjp(fields) ? "middleinput" : "bottominput"}>
                    <LocalHelp
                      markdown={`Please enter the name of the Service Provider
  you work with. If you do not work with a Service Provider, please type 'none' or
  'not applicable'.
                      `}
                    />
                  </InputGroup.Text>
                </InputGroup>

                )
              }
              {
                isSjp(fields) ? (
                  <InputGroup>
                    <Form.Control 
                      className="bottominput"
                      id="internalAgencyCode"
                      placeholder="SJP Partner Code*"
                      type="text"
                      value={fields.internalAgencyCode}
                      onChange={handleFieldChange}
                      required
                    />
                    <InputGroup.Text className="bottominput">
                      <LocalHelp
                        markdown={`Please enter your St. James' Place partner code.
It should:
- Consist of 6 numbers followed by a letter.
- Start with 09, 91-99.
- End with a letter in the range P-Z.

**For example:** \`991234Q\`

The partner code for a paraplanner or admin user may be prepended with a P or J.

**For example:** \`P991234Q\`
`}
                      />
                    </InputGroup.Text>
                  </InputGroup>
                ) : null
              }
            </div>
            <div className="text-left">
              <Form.Check 
                className="my-2"
                type="switch"
                id="otherFirms"
                value={fields.otherFirms}
                onChange={handleFieldChange}
                label="Do you currently sell or service on behalf of any other
                firms working with Cirencester Friendly Society?"
              />
              <Form.Check 
                className="my-2"
                type="switch"
                id="submittedPast"
                value={fields.submittedPast}
                onChange={handleFieldChange}
                label="Have you submitted to us in the past?"
              />
            </div>
            <div>
              <Form.Control 
                id="notes"
                placeholder="If you answered yes to any of the above two
                questions, please provide further information."
                as="textarea"
                value={fields.notes}
                onChange={handleFieldChange}
              />
              <Form.Label
                htmlFor="lastName"
                className="form-label visually-hidden"
              >
                If you answered yes to any of the above two questions, please
                provide further information.
              </Form.Label>
            </div>

            { getCaptcha() }
            <LoaderButton
              disabled={!isValid}
              size="large"
              type="submit"
              isLoading={isLoading}
            >
              Request account
            </LoaderButton>
          </div>
        </Form>
      </div>
    )
  }

  /**
   * Returns the message to show once the formdata has been submitted.
   */
  function getSubmittedMessage() {
    return (
      <Alert variant="primary">
        Your account request has been submitted. Once it has been processed by
        our team you will receive an email to sign into your account.

        For any queries or support please&nbsp;
        <a
          href="https://cirencester-friendly.co.uk/"
        >
          visit our website.
        </a>
      </Alert>
    )
  }

  return (
    <Container fluid="md">
      <div className="mediumform">
        <h3 className="mb-3 font-weight-normal text-center">Request an account</h3>
        { getRegisterCard() }
        { isSubmitted ? getSubmittedMessage() : null }
        { isSubmitted ? null : getForm() }
        <div className="d-grid gap-2 mt-2">
          <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
        </div>
      </div>
    </Container>
  );
}
